import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import ResetPasswordForm from './ResetPasswordForm';
import './AuthPage.css';
import { FiAlertTriangle, FiArrowLeft, FiLock } from 'react-icons/fi';

const ResetPasswordPage = () => {
  const [checkingSession, setCheckingSession] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    // Check URL parameters / hash for recovery indicators
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const hasRecoveryParams =
      hash.includes('type=recovery') ||
      hash.includes('access_token=') ||
      search.includes('code=');

    // Check current session
    const verifySession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!mounted) return;

        if (session) {
          setHasValidSession(true);
          setCheckingSession(false);
          return;
        }

        if (error) {
          console.warn('Session verification error:', error);
        }

        // If URL has recovery tokens, give Supabase detectSessionInUrl a brief moment
        if (hasRecoveryParams) {
          setTimeout(async () => {
            if (!mounted) return;
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (retrySession) {
              setHasValidSession(true);
            } else {
              setHasValidSession(false);
            }
            setCheckingSession(false);
          }, 1200);
        } else {
          setHasValidSession(false);
          setCheckingSession(false);
        }
      } catch (err) {
        console.error('Error verifying reset password session:', err);
        if (mounted) {
          setHasValidSession(false);
          setCheckingSession(false);
        }
      }
    };

    verifySession();

    // Listen to Supabase auth events (especially PASSWORD_RECOVERY)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || (session && (event === 'SIGNED_IN' || event === 'USER_UPDATED'))) {
        setHasValidSession(true);
        setCheckingSession(false);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [location]);

  if (checkingSession) {
    return (
      <div className="auth-container ai-gradient-header">
        <div className="auth-box" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
          <span className="loading-spinner" style={{ fontSize: '2rem', display: 'inline-block' }}>↻</span>
          <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.95rem' }}>
            Verifying your password reset link...
          </p>
        </div>
      </div>
    );
  }

  if (!hasValidSession) {
    return (
      <div className="auth-container ai-gradient-header">
        <div className="auth-box" style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#fef2f2',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '1.75rem',
            }}
          >
            <FiAlertTriangle />
          </div>

          <h2 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>
            Invalid or Expired Link
          </h2>

          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
            This password reset link is invalid, has expired, or has already been used. Please request a new link to reset your password.
          </p>

          <button
            type="button"
            className="submit-button"
            onClick={() => navigate('/auth?mode=forgot')}
            style={{ marginBottom: '0.75rem', width: '100%' }}
          >
            Request New Reset Link
          </button>

          <button
            type="button"
            className="toggle-button"
            onClick={() => navigate('/auth')}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}
          >
            <FiArrowLeft size={14} /> Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container ai-gradient-header">
      <div className="auth-box">
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#eff6ff',
            color: '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '1.5rem',
          }}
        >
          <FiLock />
        </div>

        <h2>Create New Password</h2>

        {authError && <div className="auth-error">{authError}</div>}
        {authMessage && <div className="auth-message">{authMessage}</div>}

        <ResetPasswordForm setAuthError={setAuthError} setAuthMessage={setAuthMessage} />

        <button
          type="button"
          className="toggle-button"
          onClick={() => navigate('/auth')}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', marginTop: '1.25rem' }}
        >
          <FiArrowLeft size={14} /> Back to Sign In
        </button>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
